import * as THREE from 'three';

/**
 * Scene metadata
 */
export interface SceneMetadata {
  id: string;
  name: string;
  description: string;
}

/**
 * Interface for all scenes
 */
export interface IScene {
  metadata: SceneMetadata;

  /**
   * Initialize the scene - called once when scene is created
   * @param parent The parent object (e.g., ImageAnchorGroup or Scene) to add content to
   * @param renderer The WebGL renderer
   */
  init(parent: THREE.Object3D, renderer: THREE.WebGLRenderer): void;

  /**
   * Update the scene - called every frame
   * @param delta Time since last frame in seconds
   */
  update(delta: number): void;

  /**
   * Clean up resources - called when scene is no longer needed
   */
  dispose(): void;

  /**
   * Get the root object of this scene
   */
  getRoot(): THREE.Object3D | null;

  /**
   * Set visibility of the scene
   */
  setVisible(visible: boolean): void;
}

/**
 * Abstract base class for scenes
 */
export abstract class BaseScene implements IScene {
  public metadata: SceneMetadata;
  protected root: THREE.Group;
  protected renderer: THREE.WebGLRenderer | null = null;
  protected initialized: boolean = false;

  constructor(metadata: SceneMetadata) {
    this.metadata = metadata;
    this.root = new THREE.Group();
    this.root.name = metadata.id;
  }

  /**
   * Initialize the scene - must be implemented by subclasses
   */
  abstract init(parent: THREE.Object3D, renderer: THREE.WebGLRenderer): void;

  /**
   * Update the scene - can be overridden by subclasses
   */
  abstract update(delta: number): void;

  /**
   * Dispose of scene resources - can be overridden by subclasses
   */
  dispose(): void {
    // Remove from parent
    if (this.root.parent) {
      this.root.parent.remove(this.root);
    }

    // Dispose of geometries and materials
    this.root.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        object.geometry?.dispose();
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(mat => mat.dispose());
          } else {
            object.material.dispose();
          }
        }
      }
    });

    this.initialized = false;
  }

  /**
   * Get the root object of this scene
   */
  getRoot(): THREE.Object3D {
    return this.root;
  }

  /**
   * Set visibility of the scene
   */
  setVisible(visible: boolean): void {
    this.root.visible = visible;
  }

  /**
   * Set the origin point (position/rotation/scale) of the scene
   */
  setOrigin(position?: THREE.Vector3, rotation?: THREE.Euler, scale?: THREE.Vector3): void {
    if (position) {
      this.root.position.copy(position);
    }
    if (rotation) {
      this.root.rotation.copy(rotation);
    }
    if (scale) {
      this.root.scale.copy(scale);
    }
  }

  /**
   * Get the scene's current position
   */
  getPosition(): THREE.Vector3 {
    return this.root.position.clone();
  }

  /**
   * Get the scene's current rotation
   */
  getRotation(): THREE.Euler {
    return this.root.rotation.clone();
  }

  /**
   * Get the scene's current scale
   */
  getScale(): THREE.Vector3 {
    return this.root.scale.clone();
  }

  /**
   * Get the world bounding box of the scene
   */
  getWorldBounds(): THREE.Box3 {
    const box = new THREE.Box3();
    box.setFromObject(this.root);
    return box;
  }

  /**
   * Get the center point of the scene in world coordinates
   */
  getWorldCenter(): THREE.Vector3 {
    const box = this.getWorldBounds();
    return box.getCenter(new THREE.Vector3());
  }

  /**
   * Helper to add ambient light to the scene
   */
  protected addAmbientLight(color: number = 0xffffff, intensity: number = 1): THREE.AmbientLight {
    const light = new THREE.AmbientLight(color, intensity);
    this.root.add(light);
    return light;
  }

  /**
   * Helper to add directional light to the scene
   */
  protected addDirectionalLight(
    color: number = 0xffffff,
    intensity: number = 1,
    position?: THREE.Vector3
  ): THREE.DirectionalLight {
    const light = new THREE.DirectionalLight(color, intensity);
    if (position) {
      light.position.copy(position);
    }
    this.root.add(light);
    return light;
  }
}
